import React, { createContext, useContext, useState, useEffect } from 'react';

// Define mode types
export const MODES = {
  SIMPLE: 'simple',
  ADVANCED: 'advanced'
};

// Create the context
const ModeContext = createContext();

// Hook to use the mode context
export const useMode = () => {
  const context = useContext(ModeContext);
  if (!context) {
    throw new Error('useMode must be used within a ModeProvider');
  }
  return context;
};

// ModeProvider component
export const ModeProvider = ({ children }) => {
  // Initialize mode from localStorage or default to advanced
  const [mode, setMode] = useState(() => {
    try {
      const savedMode = localStorage.getItem('financeAppMode');
      return savedMode && Object.values(MODES).includes(savedMode) ? savedMode : MODES.ADVANCED;
    } catch (error) {
      console.warn('Failed to load mode from localStorage:', error);
      return MODES.ADVANCED;
    }
  });

  // Persist mode changes to localStorage
  useEffect(() => {
    try {
      localStorage.setItem('financeAppMode', mode);
    } catch (error) {
      console.warn('Failed to save mode to localStorage:', error);
    }
  }, [mode]);

  // Toggle between simple and advanced modes
  const toggleMode = () => {
    setMode(currentMode => 
      currentMode === MODES.SIMPLE ? MODES.ADVANCED : MODES.SIMPLE
    );
  };

  // Set specific mode
  const setModeExplicit = (newMode) => {
    if (Object.values(MODES).includes(newMode)) {
      setMode(newMode);
    } else {
      console.warn('Invalid mode:', newMode);
    }
  };

  // Check if current mode is simple
  const isSimpleMode = mode === MODES.SIMPLE;
  
  // Check if current mode is advanced
  const isAdvancedMode = mode === MODES.ADVANCED;

  const value = {
    mode,
    isSimpleMode,
    isAdvancedMode,
    toggleMode,
    setMode: setModeExplicit,
    MODES
  };

  return (
    <ModeContext.Provider value={value}>
      {children}
    </ModeContext.Provider>
  );
};

export default ModeContext; 